// =============================================================================
// features/sales/campaigns/CampaignsContent.tsx — Orquestra cabeçalho,
// indicadores, filtros e lista de /campanhas (doc 26 §6.5). Montado só
// depois que o gate de permissão+flag (CampaignsPage.tsx) libera.
// =============================================================================

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { CampaignsFilters as CampaignsQueryFilters } from '../../../hooks/sales/useCampaigns';
import { useCampaigns } from '../../../hooks/sales/useCampaigns';
import { useAuth } from '../../../lib/auth-store';

import { CampaignsFilters } from './CampaignsFilters';
import { CampaignsHeader } from './CampaignsHeader';
import { CampaignsIndicators } from './CampaignsIndicators';
import { CampaignsList } from './CampaignsList';
import { useCampaignFilters } from './useCampaignFilters';

const PAGE_SIZE = 20;
const MANAGE_PERMISSION = 'sales_campaigns:manage';

export function CampaignsContent(): React.JSX.Element {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission(MANAGE_PERMISSION);
  const navigate = useNavigate();
  const goToNewCampaign = (): void => navigate('/campanhas/nova');

  const filters = useCampaignFilters();

  // exactOptionalPropertyTypes: monta o objeto só com as chaves realmente
  // presentes — atribuir `undefined` explícito a uma prop opcional é um erro
  // de tipo neste tsconfig (mesmo padrão de AgendaContent.tsx).
  const queryFilters: CampaignsQueryFilters = {
    ...(filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.productId ? { productId: [filters.productId] } : {}),
    ...(filters.mine && user ? { ownerUserId: [user.id] } : {}),
    ...(filters.createdFrom ? { createdFrom: `${filters.createdFrom}T00:00:00-04:00` } : {}),
    ...(filters.createdTo ? { createdTo: `${filters.createdTo}T23:59:59-04:00` } : {}),
    limit: PAGE_SIZE,
    offset: filters.offset,
  };

  const { data, isLoading, isError, refetch } = useCampaigns(queryFilters);

  const hasExtraFilters =
    filters.status.length > 0 ||
    filters.productId !== null ||
    filters.mine ||
    filters.createdFrom !== '' ||
    filters.createdTo !== '';

  const activeSelected = filters.status.length === 1 && filters.status[0] === 'active';

  return (
    <div className="flex flex-col gap-6">
      <CampaignsHeader canManage={canManage} />

      <CampaignsIndicators
        enabled
        activeSelected={activeSelected}
        onSelectActive={() => filters.setStatus(activeSelected ? [] : ['active'])}
      />

      <CampaignsFilters
        status={filters.status}
        onStatusChange={filters.setStatus}
        productId={filters.productId}
        onProductIdChange={filters.setProductId}
        canFilterByOwner={canManage}
        mine={filters.mine}
        onMineChange={filters.setMine}
        createdFrom={filters.createdFrom}
        createdTo={filters.createdTo}
        onCreatedRangeChange={filters.setCreatedRange}
      />

      <CampaignsList
        campaigns={data?.data ?? []}
        isLoading={isLoading}
        isError={isError}
        hasFilters={hasExtraFilters}
        currentUserId={user?.id ?? ''}
        canCreate={canManage}
        onRetry={() => void refetch()}
        onClearFilters={filters.clearAll}
        onCreate={goToNewCampaign}
        offset={filters.offset}
        limit={data?.limit ?? PAGE_SIZE}
        total={data?.total ?? 0}
        onOffsetChange={filters.setOffset}
      />
    </div>
  );
}
